import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// API's
import { recalculateWashout } from "@/Washouts/api/common/mutations";
// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
// Helpers
import { handleWashoutSubscriptions } from "@/Washouts/helpers/washoutSubscriptions";
// Constants
import actionsList from "@/Constants/actionsList";
// Custom components
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import FinanceManagerWashoutContent from "../Managers/FinanceManagerWashoutContent";
import UpdatedOn from "@/Washouts/components/UpdatedOn";
// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";
import useApproveRetractWashoutSubscription from "@/Washouts/hooks/WashoutReview/useApproveRetractWashoutSubscription";
import useApproveAllWashoutSubscription from "@/Washouts/hooks/common/useApproveAllWashoutSubscription";

const positionLabel = "Finance Director";

export default function FinanceDirectorProducerWashoutContainer(props) {
	const queryClient = useQueryClient();
	const { useWashoutQueries } = useFactoryWashout();
	const washoutQueries = useWashoutQueries(positionLabel, "Producer");

	const selected = React.useRef(null);
	const { getSubscription } = useSubscriptions();

	const subscriptionsInformation = props?.subscriptionsInformation;

	const { data: model, isLoading } = useQuery({
		queryKey: [
			"getWashout",
			props.selected?.payPeriodId,
			props.selected?.position,
			props.selected?.userId,
			props.selected?.commissionCycleId,
		],
		queryFn: async () => {
			return washoutQueries?.getWashout(
				props.location.locationId,
				props.selected.userId,
				props.selected.commissionCycleId,
				props.selected.payPeriodId,
				positionLabel
			);
		},
		enabled: !!props.location && !!props.selected,
		staleTime: Infinity,
	});

	const { mutate: recalculate } = useMutation({
		mutationKey: ["recalculateWashout"],
		mutationFn: (params) => recalculateWashout(params),
	});

	useApproveRetractWashoutSubscription({
		locationId: props.location?.locationId,
		commissionCycleId: props.selected?.commissionCycleId,
		payPeriodId: props.selected?.payPeriodId,
		updateWashoutModel: true,
	});

	useApproveAllWashoutSubscription({
		locationId: props.location?.locationId,
		commissionCycleId: props.selected?.commissionCycleId,
		payPeriodId: props.selected?.payPeriodId,
	});

	const handleSummaryChange = (input) => {
		if (input && props.location) {
			const params = {
				input,
				position: model?.employee?.position || positionLabel,
				isDepartmental: false,
				locationId: props.location.locationId,
				washoutId: model._id,
			};
			recalculate(params);
		}
	};

	React.useEffect(() => {
		selected.current = props.selected;
		handleWashoutSubscriptions({
			subscriptionsInformation,
			props: {
				...props,
				positionWithoutSpaces: "FinanceDirectorProducer",
			},
			actions: {
				queryClient,
				getSubscription,
			},
			options: {
				useRanking: true,
			},
		});

		if (props.selected) {
			props.updateSidePanelCallBack(props.selected);
		}

		return () => {
			selected.current = props.selected;
		};
	}, [props.selected, model, getSubscription, queryClient]);

	const { useAuth } = useAuthorization();
	const overrideCompensation = useAuth(actionsList.OVERRIDE_FINANCE_DIRECTOR_COMPENSATION);

	if (!model && !isLoading) {
		return (
			<RoundedBox>
				<EmptyBody
					image={props.selected ? emptyTable : empty}
					title={props.selected ? "No washout found for this director" : "Select a Finance Director to view their washout"}
					bodyHeight={"510px"}
					sx={{
						boxShadow: "none !important",
					}}
				/>
			</RoundedBox>
		);
	}

	return (
		<>
			<FinanceManagerWashoutContent
				model={model}
				locationId={props.location.locationId}
				isLoading={isLoading}
				overrideCompensation={overrideCompensation}
				editable={true}
				selected={props.selected}
				handleSummaryChange={handleSummaryChange}
				positionType={"Producer"}
			/>

			<UpdatedOn isLoading={isLoading} updatedOn={model?.created} />
		</>
	);
}

import PropTypes from "prop-types";
FinanceDirectorProducerWashoutContainer.propTypes = {
	model: PropTypes.shape({
		productConversions: PropTypes.array,
		callActivityPerformance: PropTypes.array,
	}),
	location: PropTypes.shape({
		locationId: PropTypes.string,
	}),
	selected: PropTypes.shape({
		userId: PropTypes.string,
		commissionCycleId: PropTypes.string,
		payPeriodId: PropTypes.string,
		status: PropTypes.string,
		position: PropTypes.string,
	}),
	subscriptionsInformation: PropTypes.object,
};
